import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { MIN_TAP_PX } from '../model/calendarConstants'

/**
 * The box every block on the worker calendar is drawn in — Termin, Aufgabe, Pause, Abwesenheit.
 *
 * The four used to repeat this wrapper verbatim, which is how they drifted: two were focusable, two
 * were not, and none had a name. Declared once here, a block cannot fall out of step with its
 * siblings by being edited on its own.
 *
 * Two boxes, not one. The outer box is the **target** and the inner box is the **paint**: a
 * ten-minute Pause is honestly 16px of grid, and it still catches a gloved thumb because the target
 * around it is grown to `MIN_TAP_PX` without the paint growing with it. Lying about the length to win
 * the tap would be the easier fix and the wrong one — the calendar's whole job is showing how long
 * things take.
 */

interface Props {
  /** Where the paint sits, in grid pixels. The target may start slightly above it. */
  topPx: number
  heightPx: number
  left?: string
  zIndex?: number
  /**
   * The single line that names this block. Replaces the children for a screen reader, so anything
   * the density ladder dropped has to be spelled out here — that is the point of it.
   */
  label: string
  /** The detail no block this size can show, for a pointer device. */
  title?: string
  /** Tap, click or Enter. Its presence is what makes this a button rather than a coloured rectangle. */
  onActivate?: () => void
  onPointerDown?: (e: ReactPointerEvent<HTMLDivElement>) => void
  isDragging?: boolean
  /** Pause and Abwesenheit recede: they are the day's background, not the day's work. */
  muted?: boolean
  children: ReactNode
}

export const CalendarBlockShell = ({
  topPx,
  heightPx,
  left,
  zIndex,
  label,
  title,
  onActivate,
  onPointerDown,
  isDragging = false,
  muted = false,
  children,
}: Props) => {
  const paintPx = Math.max(1, Math.round(heightPx))
  const pad = Math.max(0, Math.round((MIN_TAP_PX - paintPx) / 2))

  const draggable = Boolean(onPointerDown)
  // A drag ends in a click, so a draggable block cannot also open something on click — the pointer
  // gesture owns it. The keyboard path stays open, which is how a dragged block is still reachable.
  const clickable = Boolean(onActivate) && !draggable

  return (
    <div
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : undefined}
      aria-label={onActivate ? label : undefined}
      title={title}
      className={[
        'group absolute focus:outline-none',
        draggable ? 'cursor-grab active:cursor-grabbing select-none' : clickable ? 'cursor-pointer' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        left: left ?? '2px',
        right: 2,
        top: Math.round(topPx) - pad,
        height: paintPx + pad * 2,
        zIndex: isDragging ? 60 : zIndex ?? 10,
        // `manipulation` everywhere: a draggable block scrolls with the grid exactly like a
        // non-draggable one until `useCalendarBlockDrag`'s hold decides otherwise. `none` here
        // would hand every touch straight to the drag handler and never let the gesture scroll —
        // that used to make Pause/Abwesenheit the one thing on this calendar that didn't scroll
        // like everything else. `manipulation` also drops the 300ms tap delay.
        touchAction: 'manipulation',
        // A long press must not open the platform's own touch-callout / text-selection UI, or it
        // eats the hold before the drag gets it.
        WebkitTouchCallout: draggable ? 'none' : undefined,
        transition: isDragging ? undefined : 'top 150ms ease',
      }}
      onPointerDown={onPointerDown}
      onContextMenu={draggable ? (e) => e.preventDefault() : undefined}
      // Always stops the click, even where it activates nothing. The grid underneath places a slot
      // on click, and a drag ends in one — letting it through would drop a new appointment wherever
      // a block was just moved to.
      onClick={(e) => {
        e.stopPropagation()
        if (clickable) onActivate?.()
      }}
      onKeyDown={
        onActivate
          ? (e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return
              e.preventDefault()
              e.stopPropagation()
              onActivate()
            }
          : undefined
      }
    >
      <div
        className={[
          'absolute inset-x-0 flex flex-col overflow-hidden rounded-md shadow-sm ring-1 ring-black/10',
          'transition-[filter,opacity,box-shadow,transform] duration-100',
          'group-focus-visible:ring-2 group-focus-visible:ring-inset group-focus-visible:ring-white',
          onActivate || draggable ? 'group-active:brightness-110' : '',
          muted && !isDragging ? 'opacity-80 group-hover:opacity-100' : '',
          // The hold has nothing else to show for itself on iOS, which has no `navigator.vibrate` —
          // this lift is the only confirmation that the press told the block it was picked up.
          isDragging ? 'shadow-lg ring-2 ring-black/20 scale-[1.03]' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ top: pad, height: paintPx }}
      >
        {children}
      </div>
    </div>
  )
}
